import { useState } from 'react';
import api from '../api';
import { useApp } from '../App';
import BackButton from '../components/BackButton';
import { useI18n } from '../i18n/index.jsx';

const TYPES = [
  { value: 'income', label: '↗ Приход', color: '#10b981' },
  { value: 'expense', label: '↙ Расход', color: '#ef4444' },
];

export default function AdminBankImport() {
  const { currentCompany, user } = useApp();
  const { t } = useI18n();
  const [tab, setTab] = useState('manual'); // manual | csv
  const [entries, setEntries] = useState([makeEmpty()]);
  const [csvFile, setCsvFile] = useState(null);
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  function makeEmpty() {
    return {
      date: new Date().toISOString().split('T')[0],
      description: '',
      amount: '',
      currency: 'PLN',
      type: 'income',
    };
  }

  function updateEntry(i, field, val) {
    setEntries(prev => prev.map((e, idx) => idx === i ? { ...e, [field]: val } : e));
  }

  function addEntry() {
    setEntries(prev => [...prev, makeEmpty()]);
  }

  function removeEntry(i) {
    if (entries.length <= 1) return;
    setEntries(prev => prev.filter((_, idx) => idx !== i));
  }

  async function submitManual() {
    setMsg(''); setResults(null); setLoading(true);
    const companyId = currentCompany?.id || user?.owned_company_id || 1;
    const items = entries.map(e => ({
      date: e.date,
      description: e.description,
      amount: e.type === 'expense' ? -Math.abs(parseFloat(e.amount) || 0) : Math.abs(parseFloat(e.amount) || 0),
      currency: e.currency,
      type: e.type,
    })).filter(e => e.amount !== 0 && e.description);

    if (items.length === 0) {
      setMsg('❌ Заполните хотя бы одну запись'); setLoading(false); return;
    }

    try {
      // Create a JSON file and upload
      const blob = new Blob([JSON.stringify({ items })], { type: 'application/json' });
      const formData = new FormData();
      formData.append('file', blob, 'manual_import.json');
      const { data } = await api.post(`/accounting/admin/bank-reports/upload?company_id=${companyId}`, formData);
      setResults(data);
      setMsg(`✅ Загружено: ${data.created} транзакций` + (data.matched_proformas ? `, совпадений: ${data.matched_proformas}` : ''));
      setEntries([makeEmpty()]);
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  }

  async function submitCSV() {
    if (!csvFile) { setMsg('❌ Выберите CSV файл'); return; }
    setMsg(''); setResults(null); setLoading(true);
    const companyId = currentCompany?.id || user?.owned_company_id || 1;
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      const { data } = await api.post(`/accounting/admin/bank-reports/upload?company_id=${companyId}`, formData);
      setResults(data);
      setMsg(`✅ Загружено: ${data.created} транзакций` + (data.matched_proformas ? `, совпадений: ${data.matched_proformas}` : ''));
      setCsvFile(null);
    } catch (e) {
      setMsg('❌ ' + (e.response?.data?.detail || e.message));
    }
    setLoading(false);
  }

  return (
    <div className="page">
      <BackButton />
      <h1>🏦 Импорт банковской выписки</h1>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {[
          { id: 'manual', label: '✏️ Вручную' },
          { id: 'csv', label: '📁 CSV файл' },
        ].map(opt => (
          <button key={opt.id} onClick={() => setTab(opt.id)} style={{
            flex: 1, padding: '14px 16px', borderRadius: 14, border: 'none',
            fontSize: 16, fontWeight: 700, cursor: 'pointer',
            background: tab === opt.id ? 'linear-gradient(135deg, #3b82f6, #6366f1)' : 'rgba(255,255,255,0.06)',
            color: '#fff',
          }}>
            {opt.label}
          </button>
        ))}
      </div>

      {msg && (
        <div style={{
          padding: 14, borderRadius: 12, marginBottom: 16, fontWeight: 600, fontSize: 15,
          background: msg.startsWith('✅') ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
          color: msg.startsWith('✅') ? '#10b981' : '#ef4444',
          border: `1px solid ${msg.startsWith('✅') ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)'}`,
        }}>
          {msg}
        </div>
      )}

      {/* Manual entry */}
      {tab === 'manual' && (
        <div>
          {entries.map((entry, i) => (
            <div key={i} style={{
              background: 'var(--card)', borderRadius: 16, padding: 18,
              marginBottom: 12, border: '1px solid rgba(255,255,255,0.06)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontWeight: 700, fontSize: 16 }}>#{i + 1}</span>
                {entries.length > 1 && (
                  <button onClick={() => removeEntry(i)} style={{
                    background: 'rgba(239,68,68,0.2)', border: 'none', borderRadius: 10,
                    padding: '8px 14px', color: '#ef4444', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                  }}>🗑 Удалить</button>
                )}
              </div>

              {/* Type selector */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                {TYPES.map(opt => (
                  <button key={opt.value} onClick={() => updateEntry(i, 'type', opt.value)} style={{
                    flex: 1, padding: '12px 8px', borderRadius: 12,
                    fontSize: 15, fontWeight: 700, cursor: 'pointer',
                    background: entry.type === opt.value ? opt.color + '33' : 'rgba(255,255,255,0.04)',
                    color: entry.type === opt.value ? opt.color : 'rgba(255,255,255,0.5)',
                    border: entry.type === opt.value ? `2px solid ${opt.color}` : '2px solid transparent',
                  }}>
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="form">
                <div className="field">
                  <label>📅 Дата</label>
                  <input type="date" value={entry.date} onChange={e => updateEntry(i, 'date', e.target.value)} />
                </div>
                <div className="field">
                  <label>📝 Описание (отправитель / назначение)</label>
                  <input placeholder="Nataliia Kondratovets — umowa o Dzieło" value={entry.description}
                    onChange={e => updateEntry(i, 'description', e.target.value)} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
                  <div className="field">
                    <label>💰 Сумма</label>
                    <input type="number" step="0.01" placeholder="1200.00" value={entry.amount}
                      onChange={e => updateEntry(i, 'amount', e.target.value)}
                      style={{ fontSize: 20, fontWeight: 800, padding: '16px' }} />
                  </div>
                  <div className="field">
                    <label>💱 Валюта</label>
                    <select value={entry.currency} onChange={e => updateEntry(i, 'currency', e.target.value)}
                      style={{ fontSize: 18, fontWeight: 700 }}>
                      <option value="PLN">🇵🇱 PLN</option>
                      <option value="EUR">🇪🇺 EUR</option>
                      <option value="USD">🇺🇸 USD</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <button onClick={addEntry} style={{
            width: '100%', padding: 16, borderRadius: 14, border: '2px dashed rgba(255,255,255,0.15)',
            background: 'transparent', color: 'rgba(255,255,255,0.5)', fontSize: 16, fontWeight: 700,
            cursor: 'pointer', marginBottom: 16,
          }}>
            + Добавить ещё запись
          </button>

          <button onClick={submitManual} disabled={loading} className="btn btn-primary" style={{
            width: '100%', padding: 18, fontSize: 18, fontWeight: 800,
          }}>
            {loading ? '⏳ Загрузка...' : `📤 Загрузить ${entries.length} ${entries.length === 1 ? 'запись' : 'записей'}`}
          </button>
        </div>
      )}

      {/* CSV upload */}
      {tab === 'csv' && (
        <div style={{
          background: 'var(--card)', borderRadius: 16, padding: 24,
          border: '1px solid rgba(255,255,255,0.06)',
        }}>
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <div style={{ fontSize: 48, marginBottom: 8 }}>📁</div>
            <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Загрузите CSV выписку</div>
            <div style={{ fontSize: 14, opacity: 0.5, lineHeight: 1.5 }}>
              Формат: CSV из Santander Centrum24, mBank, ING, PKO BP<br />
              Или JSON с полями: date, description, amount, currency
            </div>
          </div>

          <label style={{
            display: 'block', width: '100%', padding: 20, borderRadius: 14,
            border: '2px dashed rgba(99,102,241,0.4)', background: 'rgba(99,102,241,0.08)',
            textAlign: 'center', cursor: 'pointer', marginBottom: 16,
          }}>
            <input type="file" accept=".csv,.json,.txt" style={{ display: 'none' }}
              onChange={e => setCsvFile(e.target.files[0])} />
            <div style={{ fontSize: 16, fontWeight: 700, color: '#6366f1' }}>
              {csvFile ? `📄 ${csvFile.name}` : '📎 Нажмите чтобы выбрать файл'}
            </div>
          </label>

          <button onClick={submitCSV} disabled={loading || !csvFile} className="btn btn-primary" style={{
            width: '100%', padding: 18, fontSize: 18, fontWeight: 800,
          }}>
            {loading ? '⏳ Загрузка...' : '📤 Загрузить выписку'}
          </button>
        </div>
      )}

      {/* Results */}
      {results && (
        <div style={{
          background: 'rgba(16,185,129,0.08)', borderRadius: 16, padding: 18, marginTop: 16,
          border: '1px solid rgba(16,185,129,0.2)',
        }}>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>📊 Результат</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 12, textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 800 }}>{results.created}</div>
              <div style={{ fontSize: 12, opacity: 0.6 }}>Загружено</div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 12, textAlign: 'center' }}>
              <div style={{ fontSize: 24, fontWeight: 800 }}>{results.matched_proformas || 0}</div>
              <div style={{ fontSize: 12, opacity: 0.6 }}>Совпадений с проформами</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
